import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, History, RotateCcw } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DangerDialog } from '@/components/DangerDialog'
import { fetchFeatureFlags, fetchFlagHistory, setFeatureFlag, type AdminFeatureFlag, type FlagHistoryRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import { PermissionDenied } from '@/components/States'

function fmt(ts: string | number): string {
  const n = typeof ts === 'number' ? ts : Number(ts)
  if (!Number.isFinite(n)) return '—'
  try {
    return new Date(n).toLocaleString()
  } catch {
    return '—'
  }
}

/**
 * A14.13 + A14.16 — Feature-flag o'zgarish tarixi + rollback. Manba = audit
 * jurnali (SetFlag har yozuvni qayd etadi), alohida jadval YO'Q. Eng yuqori yozuv
 * = joriy qiymat; istalgan eski qiymatni «Qaytarish» (rollback) — L2 DangerDialog
 * bilan qayta qo'llaydi (`POST /admin/settings/flags`, u ham audit'ga tushadi →
 * tarix haqqoniy qoladi). AUDIT_VIEW-gated (yozuvni backend admin-gate qiladi).
 */
export function FlagHistory() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const selected = params.get('key') ?? ''
  const [flags, setFlags] = useState<AdminFeatureFlag[] | null>(null)
  const [rows, setRows] = useState<FlagHistoryRow[] | null>(null)
  const [restore, setRestore] = useState<FlagHistoryRow | null>(null)

  useEffect(() => {
    fetchFeatureFlags({ filters: [], sort: [], page: 1, pageSize: 500, search: '', signal: new AbortController().signal })
      .then((r) => setFlags(r.rows))
      .catch(() => setFlags([]))
  }, [])

  const loadHistory = useCallback((key: string) => {
    if (!key) {
      setRows(null)
      return
    }
    setRows(null)
    fetchFlagHistory(key)
      .then(setRows)
      .catch(() => setRows([]))
  }, [])

  useEffect(() => {
    loadHistory(selected)
  }, [selected, loadHistory])

  const selectKey = (key: string) => {
    if (key) setParams({ key }, { replace: true })
    else setParams({}, { replace: true })
  }

  if (!can(PERMISSIONS.AUDIT_VIEW)) return <PermissionDenied permission={PERMISSIONS.AUDIT_VIEW} />

  return (
    <>
      <PageHeader
        title={t.flags.historyTitle}
        screenId="A14.13"
        actions={
          <Link
            to="/flags"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.flags.title}
          </Link>
        }
      />
      <div className="border-b border-border-subtle p-3">
        <label className="mb-1 block text-xs text-tertiary">{t.flags.key}</label>
        <select
          value={selected}
          onChange={(e) => selectKey(e.target.value)}
          className="w-full max-w-md rounded-md border border-border-default bg-surface-2 px-2.5 py-1.5 font-mono text-sm text-primary outline-none"
        >
          <option value="">— {t.flags.pickFlag} —</option>
          {(flags ?? []).map((f) => (
            <option key={f.key} value={f.key}>{f.key}</option>
          ))}
        </select>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {!selected ? (
          <div className="px-3 py-8 text-center text-sm text-tertiary">
            <History size={24} className="mx-auto mb-2 opacity-60" />
            {t.flags.pickFlag}
          </div>
        ) : rows === null ? (
          <div className="p-6 text-center text-sm text-tertiary">…</div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-tertiary">
            <History size={24} className="mx-auto mb-2 opacity-60" />
            {t.flags.historyEmpty}
          </div>
        ) : (
          <ol className="max-w-2xl space-y-2">
            {rows.map((r, i) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-surface-1 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-surface-3 px-2 py-0.5 font-mono text-sm text-primary">{r.value ?? '—'}</span>
                    {i === 0 && (
                      <span className="rounded bg-accent-subtle px-1.5 py-0.5 text-xs text-accent">{t.flags.current}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-tertiary">
                    {(r.admin ?? '—')} · {fmt(r.created_at)}
                  </div>
                </div>
                {i !== 0 && (
                  <button
                    onClick={() => setRestore(r)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-3 py-1.5 text-sm text-secondary hover:text-primary"
                  >
                    <RotateCcw size={14} />
                    {t.flags.restore}
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
      <DangerDialog
        open={restore !== null}
        onClose={() => setRestore(null)}
        screenId="A14.13"
        level={2}
        title={t.flags.restoreTitle}
        description={restore ? t.flags.restoreDesc(selected, restore.value ?? '—') : ''}
        confirmLabel={t.flags.restore}
        onConfirm={async () => {
          if (!restore) return
          try {
            await setFeatureFlag(selected, { value: restore.value ?? '' })
            toast(t.flags.restored, { tone: 'success' })
            loadHistory(selected)
          } catch (e) {
            toast(t.flags.toggleFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
            throw e
          } finally {
            setRestore(null)
          }
        }}
      />
    </>
  )
}
